import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearPractitionerSelection, isDentalCleaning, isRoleAllowed, practitioners } from '../bookingPractitioners';
import { Header } from '../Header';

export function PractitionersPage() {
  localStorage.setItem('headerPhase', '2');

  const navigate = useNavigate();

  const procedureId = localStorage.getItem('selectedProcedureId')?.trim() || '';
  let procedureName: string | undefined;
  try {
    procedureName = JSON.parse(localStorage.getItem('procedureCache') || '{}')[procedureId]?.name;
  } catch {
    // The known cleaning ID remains usable without a cached display name.
  }
  const dentalCleaning = isDentalCleaning(procedureId, procedureName);
  // Temporary frontend preview: real procedure approvals and the DH/PA IDs
  // still need confirmation. Do not treat this presentation filter as an
  // authorization check; availability and booking retain their backend checks.
  const eligiblePractitioners = useMemo(() => practitioners.filter((practitioner) =>
    Boolean(procedureId) && isRoleAllowed(practitioner, dentalCleaning)
  ), [procedureId, dentalCleaning]);

  useEffect(() => {
    const selectedId = localStorage.getItem('selectedPractitionerId');
    if (selectedId && !eligiblePractitioners.some((practitioner) => practitioner.id === selectedId)) {
      clearPractitionerSelection();
    }
  }, [eligiblePractitioners]);

  const selectPractitioner = (practitionerId: string) => {
    if (!eligiblePractitioners.some((practitioner) => practitioner.id === practitionerId)) return;
    if (localStorage.getItem('selectedPractitionerMode') === 'any' ||
      localStorage.getItem('selectedPractitionerId') !== practitionerId) {
      clearPractitionerSelection();
    }
    localStorage.setItem('previousPage', '/practitioners');
    localStorage.setItem('selectedPractitionerId', practitionerId);
    navigate('/date-time-entry');
  };

  const back = () => navigate('/procedures');

  return (
    <div className="booking-practitioners min-h-screen bg-[#fbf7f1] text-slate-800">
      <Header compact />
      <div className="booking-practitioners__content mx-auto flex min-h-screen max-w-6xl flex-col px-6 py-12">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-slate-900">
            Wählen Sie Ihre Behandlerin oder Ihren Behandler
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Persönliche Betreuung durch unsere erfahrenen Experten.
          </p>
        </div>

        <div className="booking-practitioners__tiles mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {eligiblePractitioners.map((practitioner) => (
            <button
              key={practitioner.name}
              type="button"
              onClick={() => selectPractitioner(practitioner.id)}
              className="booking-practitioners__card flex h-full cursor-pointer flex-col overflow-hidden rounded-3xl border border-[#efe7da] bg-white text-left shadow-[0_18px_40px_rgba(19,19,19,0.05)] transition hover:-translate-y-0.5 hover:border-[#d9c39b]"
            >
              <div className="booking-practitioners__photo aspect-[10/9] w-full overflow-hidden bg-white">
                <img
                  src={practitioner.image}
                  alt={practitioner.name}
                  className="h-full w-full object-cover object-center"
                />
              </div>
              <div className="booking-practitioners__info flex flex-1 flex-col px-6 pb-6 pt-4">
                <span className="w-fit rounded-full bg-[#f7f2e8] px-3 py-1 text-xs text-[#c4a67b]">
                  {practitioner.role}
                </span>
                <h2 className="mt-3 text-base font-semibold text-slate-900">{practitioner.name}</h2>
                {practitioner.details && (
                  <p className="mt-1 text-xs text-slate-400">{practitioner.details}</p>
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="booking-practitioners__actions mt-10 flex justify-start gap-3">
          <button
            type="button"
            onClick={back}
            className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-[#cbb58a] bg-[#cbb58a] px-6 py-3 text-sm font-semibold text-white transition hover:border-[#bca477] hover:bg-[#bca477]"
          >
            ← Zurück
          </button>
        </div>
      </div>
    </div>
  );
}
